import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { Refresh, Visibility, Phone, WhatsApp } from '@mui/icons-material';
import flightQuoteService from '../../services/api/flightQuoteService';
import { whatsAppLink, buildPriceConfirmedWhatsApp, buildFollowUpWhatsApp } from '../../utils/flightQuoteWhatsApp';
import { FlightQuoteStatusChip } from '../../utils/flightQuoteStatus';
import {
  QUOTE_CURRENCY_OPTIONS,
  normalizeQuoteCurrency,
  formatQuoteAmount,
} from '../../utils/quoteCurrencies';

const whatsAppLinkSimple = (phone) => whatsAppLink(phone);

const isValidEmail = (value) =>
  typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const resolveRequestId = (row) => row?.requestId || row?.RequestId || row?.id;

const FlightQuoteRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: 'PendingAgent',
    confirmedAmount: '',
    confirmedCurrency: 'USD',
    agentMessage: '',
    notifyEmail: true,
    notifyWhatsApp: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveSeverity, setSaveSeverity] = useState('info');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await flightQuoteService.listQuoteRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load flight quote requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetail = async (row) => {
    setSelected(row);
    setDetail(null);
    setSaveMsg('');
    setSaveSeverity('info');
    setUpdateForm({
      status: row.status || 'PendingAgent',
      confirmedAmount: row.confirmedAmount || row.shownAmount || '',
      confirmedCurrency: normalizeQuoteCurrency(row.confirmedCurrency || row.shownCurrency, 'USD'),
      agentMessage: row.agentMessage || '',
      notifyEmail: isValidEmail(row.customerEmail),
      notifyWhatsApp: false,
    });
    setDetailLoading(true);
    try {
      // Prefer list payload (includes offerSnapshotJson after API update)
      if (row.offerSnapshotJson) {
        setDetail({ payload: row, fromList: true });
        return;
      }
      const data = await flightQuoteService.getQuoteRequest(resolveRequestId(row));
      setDetail(data);
    } catch (err) {
      // Still show summary from table row even if detail fetch fails
      setDetail({
        payload: row,
        fromList: true,
        warning: err.response?.data?.message || 'Full offer snapshot unavailable — showing summary from queue.',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const getOfferSnapshot = (detail, selected) => {
    const json =
      detail?.payload?.offerSnapshotJson ||
      detail?.payload?.OfferSnapshotJson ||
      selected?.offerSnapshotJson;
    if (!json) return null;
    try {
      return typeof json === 'string' ? JSON.parse(json) : json;
    } catch {
      return null;
    }
  };

  const saveUpdate = async (options = {}) => {
    const requestId = resolveRequestId(selected);
    if (!requestId) {
      setSaveSeverity('error');
      setSaveMsg('Missing request ID — close and reopen this quote.');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      const parsedAmount = updateForm.confirmedAmount
        ? parseFloat(String(updateForm.confirmedAmount).replace(/,/g, '').trim())
        : null;
      const notifyEmail = options.notifyEmail ?? updateForm.notifyEmail;
      const notifyWhatsApp = options.notifyWhatsApp ?? updateForm.notifyWhatsApp;
      const res = await flightQuoteService.updateQuoteRequest(requestId, {
        status: updateForm.status,
        confirmedAmount:
          parsedAmount != null && Number.isFinite(parsedAmount) ? String(parsedAmount) : null,
        confirmedCurrency: normalizeQuoteCurrency(updateForm.confirmedCurrency, 'USD'),
        agentMessage: updateForm.agentMessage || null,
        notifyEmail: Boolean(notifyEmail) && isValidEmail(selected?.customerEmail),
        notifyWhatsApp: Boolean(notifyWhatsApp),
      });
      const quote = res.quote || {};
      setSelected((prev) => (prev ? { ...prev, ...quote, status: quote.status || updateForm.status } : prev));
      setUpdateForm((prev) => ({
        ...prev,
        status: quote.status || prev.status,
        confirmedAmount: quote.confirmedAmount ?? prev.confirmedAmount,
        confirmedCurrency: quote.confirmedCurrency ?? prev.confirmedCurrency,
      }));
      let msg = res.message || 'Quote updated.';
      if (res.notified) msg += ' Customer notified.';
      else if (notifyEmail || notifyWhatsApp) msg += ' Saved without notification (email/WhatsApp off or failed).';
      if (res.notificationWarning) msg += ` ${res.notificationWarning}`;
      setSaveSeverity(res.notificationWarning ? 'warning' : 'success');
      setSaveMsg(msg);
      await load();
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED';
      setSaveSeverity('error');
      setSaveMsg(
        isTimeout
          ? 'Request timed out. The quote may still be saved — refresh the list. Turn off “Send email” if SMTP is slow.'
          : err.response?.data?.message || err.message || 'Update failed.'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Flight fare requests
          </Typography>
          <Typography color="text.secondary">
            Customer “Request this fare” submissions — book manually on the IATA portal, then contact the customer by phone or WhatsApp.
          </Typography>
        </Box>
        <IconButton onClick={load} aria-label="Refresh">
          <Refresh />
        </IconButton>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Update status and confirmed price here, then notify the customer by email (SMTP) or WhatsApp API if configured. Free option: use the WhatsApp button to message manually.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Fare shown</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No flight quote requests yet.
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((row) => (
                  <TableRow key={row.requestId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {String(row.requestId).slice(0, 8)}…
                    </TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {row.customerPhone}
                        {row.customerPhone && (
                          <>
                            <IconButton
                              size="small"
                              component="a"
                              href={`tel:${row.customerPhone}`}
                              aria-label="Call customer"
                            >
                              <Phone fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              component="a"
                              href={whatsAppLinkSimple(row.customerPhone)}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="WhatsApp customer"
                            >
                              <WhatsApp fontSize="small" color="success" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                      {row.customerEmail && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.customerEmail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.origin} → {row.destination}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.departureDate}
                        {row.returnDate ? ` · return ${row.returnDate}` : ''}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {row.airlineName} {row.flightNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatQuoteAmount(row.shownAmount, row.shownCurrency)}
                    </TableCell>
                    <TableCell>
                      <FlightQuoteStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openDetail(row)} aria-label="View details">
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Flight quote request</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{selected.customerName}</strong> · {selected.customerPhone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.origin} → {selected.destination} · {selected.airlineName} {selected.flightNumber}
              </Typography>
              {selected.notes && (
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  <strong>Customer notes:</strong> {selected.notes}
                </Typography>
              )}
            </Box>
          )}
          {selected && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Update &amp; notify customer</Typography>
              <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  label="Status"
                  size="small"
                  sx={{ minWidth: 160 }}
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <MenuItem value="PendingAgent">Pending</MenuItem>
                  <MenuItem value="Contacted">Contacted</MenuItem>
                  <MenuItem value="PriceConfirmed">Price confirmed</MenuItem>
                  <MenuItem value="Booked">Booked</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </TextField>
                <TextField
                  label="Confirmed price"
                  size="small"
                  value={updateForm.confirmedAmount}
                  onChange={(e) => setUpdateForm((p) => ({ ...p, confirmedAmount: e.target.value }))}
                />
                <TextField
                  select
                  label="Currency"
                  size="small"
                  sx={{ minWidth: 120 }}
                  value={updateForm.confirmedCurrency || 'USD'}
                  onChange={(e) => setUpdateForm((p) => ({ ...p, confirmedCurrency: e.target.value }))}
                  helperText={
                    selected?.shownCurrency
                      ? `Customer saw: ${formatQuoteAmount(selected.shownAmount, selected.shownCurrency)}`
                      : undefined
                  }
                >
                  {[
                    ...new Set([
                      updateForm.confirmedCurrency,
                      normalizeQuoteCurrency(selected?.shownCurrency, 'USD'),
                      ...QUOTE_CURRENCY_OPTIONS,
                    ].filter(Boolean)),
                  ].map((code) => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Or type ISO code"
                  size="small"
                  sx={{ minWidth: 110 }}
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                  value={updateForm.confirmedCurrency || ''}
                  onChange={(e) =>
                    setUpdateForm((p) => ({
                      ...p,
                      confirmedCurrency: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3),
                    }))
                  }
                />
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Message to customer"
                placeholder="Your confirmed fare is USD 145. Please call us to pay."
                sx={{ mt: 2 }}
                value={updateForm.agentMessage}
                onChange={(e) => setUpdateForm((p) => ({ ...p, agentMessage: e.target.value }))}
              />
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={updateForm.notifyEmail} onChange={(e) => setUpdateForm((p) => ({ ...p, notifyEmail: e.target.checked }))} />}
                  label="Send email"
                />
                <FormControlLabel
                  control={<Checkbox checked={updateForm.notifyWhatsApp} onChange={(e) => setUpdateForm((p) => ({ ...p, notifyWhatsApp: e.target.checked }))} />}
                  label="Send WhatsApp (API)"
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={saving}
                  onClick={() => saveUpdate({ notifyEmail: false, notifyWhatsApp: false })}
                >
                  {saving ? 'Saving…' : 'Approve / save quote'}
                </Button>
                <Button
                  variant="outlined"
                  disabled={saving}
                  onClick={() => saveUpdate()}
                >
                  Save &amp; notify customer
                </Button>
              </Stack>
              {saveMsg && <Alert severity={saveSeverity} sx={{ mt: 2 }}>{saveMsg}</Alert>}
            </Box>
          )}
          {detailLoading && <CircularProgress size={24} />}
          {detail?.warning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {detail.warning}
            </Alert>
          )}
          {detail?.error && <Alert severity="error">{detail.error}</Alert>}
          {getOfferSnapshot(detail, selected) && (
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 360,
                fontSize: 12,
              }}
            >
              {JSON.stringify(getOfferSnapshot(detail, selected), null, 2)}
            </Box>
          )}
          {!detailLoading && !getOfferSnapshot(detail, selected) && !detail?.error && (
            <Alert severity="info">
              Request ID: <strong>{selected?.requestId}</strong>
              <br />
              Shown fare: {selected?.shownCurrency} {selected?.shownAmount}
              <br />
              Offer ID: {selected?.offerId || '—'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          {selected?.customerPhone && (
            <>
              <Button
                component="a"
                href={whatsAppLink(selected.customerPhone, buildFollowUpWhatsApp(selected))}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                color="success"
                variant="outlined"
              >
                WhatsApp — follow up
              </Button>
              <Button
                component="a"
                href={whatsAppLink(selected.customerPhone, buildPriceConfirmedWhatsApp(selected, updateForm))}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                color="success"
              >
                WhatsApp — price confirmed
              </Button>
            </>
          )}
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FlightQuoteRequestsPage;
